/**
 * Revive Adserver Integration Service
 * Handles communication with Revive Adserver for fashion ads
 */

export interface ReviveAdConfig {
  enabled: boolean;
  serverUrl: string;
  zones: {
    header: number;
    sidebar: number;
    mobile: number;
    inContent: number;
    footer: number;
  };
  fallbackAds: {
    enabled: boolean;
    defaultBanners: string[];
  };
}

export interface ReviveAdResponse {
  html: string;
  bannerid: string;
  campaignid: string;
  zoneid: string;
  clickUrl?: string;
  imageUrl?: string;
  alt?: string;
}

export interface AdSlotConfig {
  zoneId: number;
  width: number;
  height: number;
  containerId: string;
  fallbackContent?: string;
}

class ReviveAdService {
  private config: ReviveAdConfig | null = null;
  private loadedScripts: Set<string> = new Set();
  private adServerUrl: string = process.env.REACT_APP_CUSTOM_AD_SERVER_URL || process.env.REACT_APP_REVIVE_SERVER_URL || 'http://localhost:3000';

  /**
   * Initialize the Revive Ad Service with configuration
   */
  async initialize(config: ReviveAdConfig): Promise<void> {
    this.config = config;
    
    if (!config.enabled) {
      console.log('Revive Adserver is disabled');
      return;
    }

    // Validate server URL
    if (!this.isValidUrl(config.serverUrl)) {
      throw new Error('Invalid Revive Adserver URL');
    }

    // Test server connectivity
    await this.testServerConnection();
  }

  /**
   * Test if the ad server is accessible
   */
  private async testServerConnection(): Promise<boolean> {
    if (!this.config?.enabled || !this.config.serverUrl) {
      return false;
    }

    try {
      const response = await fetch(`${this.adServerUrl}/api/health`, {
        method: 'GET',
        cache: 'no-cache'
      });
      
      return response.ok;
    } catch (error) {
      console.warn('Ad server connection test failed:', error);
      return false;
    }
  }

  /**
   * Load an ad for a specific zone
   */
  async loadAd(slotConfig: AdSlotConfig): Promise<boolean> {
    if (!this.config?.enabled) {
      this.loadFallbackAd(slotConfig);
      return false;
    }

    try {
      const adResponse = await this.fetchAdContent(slotConfig.zoneId);
      const container = document.getElementById(slotConfig.containerId);
      
      if (container && adResponse) {
        container.innerHTML = adResponse;
        this.trackAdImpression(slotConfig.zoneId);
        return true;
      } else {
        this.loadFallbackAd(slotConfig);
        return false;
      }
    } catch (error) {
      console.error('Failed to load ad:', error);
      this.loadFallbackAd(slotConfig);
      return false;
    }
  }

  /**
   * Load ad by fetching from custom ad server and injecting HTML directly
   */
  async loadAdScript(slotConfig: AdSlotConfig): Promise<boolean> {
    if (!this.config?.enabled) {
      this.loadFallbackAd(slotConfig);
      return false;
    }

    const container = document.getElementById(slotConfig.containerId);
    if (!container) {
      return false;
    }

    try {
      const url = `${this.adServerUrl}/api/ads/serve?zoneId=${slotConfig.zoneId}`;
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        if (data && data.html) {
          container.innerHTML = data.html;
          this.trackAdImpression(slotConfig.zoneId);
          return true;
        }
      }

      // No ad available or error
      this.loadFallbackAd(slotConfig);
      return false;
    } catch (error) {
      console.error('Failed to load ad from custom ad server:', error);
      this.loadFallbackAd(slotConfig);
      return false;
    }
  }

  /**
   * Fetch ad content from custom ad server
   */
  private async fetchAdContent(zoneId: number): Promise<string | null> {
    try {
      const url = `${this.adServerUrl}/api/ads/serve?zoneId=${zoneId}`;
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        return data.html || null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching ad content:', error);
      return null;
    }
  }

  /**
   * Load fallback ad content when Revive is unavailable
   */
  private loadFallbackAd(slotConfig: AdSlotConfig): void {
    const container = document.getElementById(slotConfig.containerId);
    if (!container) return;

    if (slotConfig.fallbackContent) {
      container.innerHTML = slotConfig.fallbackContent;
    } else if (this.config?.fallbackAds.enabled && this.config.fallbackAds.defaultBanners.length > 0) {
      const randomBanner = this.config.fallbackAds.defaultBanners[
        Math.floor(Math.random() * this.config.fallbackAds.defaultBanners.length)
      ];
      container.innerHTML = randomBanner;
    } else {
      // Default fashion app promotion
      container.innerHTML = this.getDefaultFashionAd(slotConfig.width, slotConfig.height);
    }
  }

  /**
   * Generate default fashion app promotional ad
   */
  private getDefaultFashionAd(width: number, height: number): string {
    const isLarge = width >= 300 && height >= 200;
    const isMobile = width <= 320;

    if (isMobile) {
      return `
        <div style="width:${width}px;height:${height}px;background:linear-gradient(90deg,#ff6b6b,#4ecdc4);display:flex;align-items:center;color:white;font-family:Arial,sans-serif;cursor:pointer;border-radius:4px;" onclick="window.location.href='/upload'">
          <div style="margin-left:12px;flex:1;">
            <strong style="font-size:12px;">Hooliah</strong>
            <div style="font-size:9px;opacity:0.9;">Rate Your Style</div>
          </div>
          <div style="margin-right:12px;background:rgba(255,255,255,0.2);padding:4px 8px;border-radius:12px;font-size:10px;font-weight:bold;">
            Upload →
          </div>
        </div>
      `;
    } else if (isLarge) {
      return `
        <div style="width:${width}px;height:${height}px;background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);position:relative;font-family:Arial,sans-serif;cursor:pointer;border-radius:8px;overflow:hidden;" onclick="window.location.href='/search'">
          <div style="position:absolute;top:50%;left:20px;transform:translateY(-50%);color:white;">
            <h3 style="margin:0;font-size:18px;font-weight:bold;">Discover Fashion Trends</h3>
            <p style="margin:5px 0;font-size:14px;opacity:0.9;">AI-powered style analysis</p>
            <div style="background:rgba(255,255,255,0.2);padding:8px 16px;border-radius:20px;display:inline-block;margin-top:10px;font-size:12px;font-weight:bold;">
              Explore Now →
            </div>
          </div>
        </div>
      `;
    } else {
      return `
        <div style="width:${width}px;height:${height}px;background:linear-gradient(45deg,#ff6b6b,#4ecdc4);display:flex;align-items:center;justify-content:center;color:white;font-family:Arial,sans-serif;cursor:pointer;border-radius:4px;" onclick="window.location.href='/'">
          <div style="text-align:center;">
            <div style="font-size:14px;font-weight:bold;">Hooliah</div>
            <div style="font-size:10px;margin-top:2px;">Rate & Discover Style</div>
          </div>
        </div>
      `;
    }
  }

  /**
   * Track ad impression for analytics
   */
  private trackAdImpression(zoneId: number): void {
    // The impression pixel is embedded in the ad HTML from the custom ad server,
    // so actual tracking is handled by the pixel firing on load.
    // This method provides a fallback zone-based tracking signal.
    const trackingUrl = `${this.adServerUrl}/api/ads/impression?adId=zone-${zoneId}&campaignId=unknown`;
    const img = new Image();
    img.src = trackingUrl;

    // Also track in our own analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'ad_impression', {
        event_category: 'ads',
        event_label: `custom_zone_${zoneId}`,
        value: 1
      });
    }
  }

  /**
   * Track ad click for analytics
   */
  trackAdClick(zoneId: number, clickUrl?: string): void {
    // The click tracking is handled by the ad HTML's click URL which routes
    // through /api/ads/click on the custom ad server. Here we just open the
    // click URL directly (the redirect handles tracking).
    if (clickUrl) {
      window.open(clickUrl, '_blank');
    }

    // Track in our own analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'ad_click', {
        event_category: 'ads',
        event_label: `custom_zone_${zoneId}`,
        value: 1
      });
    }
  }

  /**
   * Refresh ads in all containers
   */
  async refreshAllAds(): Promise<void> {
    if (!this.config?.enabled) return;

    const adContainers = document.querySelectorAll('[data-revive-zone]');
    
    // Convert NodeList to Array for iteration
    const containerArray = Array.from(adContainers);
    
    for (const container of containerArray) {
      const zoneId = parseInt(container.getAttribute('data-revive-zone') || '0');
      const width = parseInt(container.getAttribute('data-width') || '300');
      const height = parseInt(container.getAttribute('data-height') || '250');
      
      if (zoneId > 0) {
        await this.loadAdScript({
          zoneId,
          width,
          height,
          containerId: container.id
        });
      }
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): ReviveAdConfig | null {
    return this.config;
  }

  /**
   * Update configuration
   */
  async updateConfig(newConfig: ReviveAdConfig): Promise<void> {
    await this.initialize(newConfig);
  }

  /**
   * Check if service is enabled and configured
   */
  isEnabled(): boolean {
    return this.config?.enabled === true;
  }

  /**
   * Validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get available zones from configuration
   */
  getAvailableZones(): Record<string, number> {
    return this.config?.zones || {};
  }

  /**
   * Test specific zone availability
   */
  async testZone(zoneId: number): Promise<boolean> {
    if (!this.config?.enabled) {
      return false;
    }

    try {
      const testUrl = `${this.adServerUrl}/api/ads/serve?zoneId=${zoneId}`;
      const response = await fetch(testUrl, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const reviveAdService = new ReviveAdService();
export default reviveAdService;